import React from 'react';
const { useState, useEffect } = React;
import { cardSt, mutedSt, badgeSt, warnSt, fmtDate, SubTabs } from '../../diagnostic/shared.jsx';
import { BRAND_TEMPLATES, BRAND_FORMATS, BRAND_CHANNELS, DEFAULT_BRAND_PROFILE, SELF_FRAME_DEFAULT, webinarWhen } from '../../../brandConfig.js';
import { ownerCall, Notice } from '../shared.jsx';
import { LoadingState, ErrorState, EmptyState } from '../../ui/States.jsx';
import BrandProfile from './BrandProfile.jsx';
import BrandPhotos from './BrandPhotos.jsx';
import PieceEditor, { newPiece } from './PieceEditor.jsx';
import PiecePreview from './PiecePreview.jsx';
import { loadPhoto, exampleImage, brandFontsReady } from './brandClient.js';
import { exampleFigure } from './brandExamples.js';
import { chipSt } from '../shared.jsx';

// Mi marca: Ale's personal brand (Alejandra Cadario), inside Mi espacio.
// The brand identity is applied ONLY to the pieces drawn on the canvas: these
// screens keep Oike's design. Templates are originals (src/brandConfig.js);
// each piece is a copy that keeps its template and brand versions. Nothing is
// published or scheduled: pieces are exported as PNG and the text is copied.

const STATUS = { draft: 'Borrador', approved: 'Aprobada', archived: 'Archivada' };

// Example content for the catalog (clearly marked as an example): it tells no experience of Ale's
const EXAMPLE = {
  post: { kicker: 'Etapas de cambio', headline: 'Lo que traje en la valija también cuenta', body: 'Antes de pensar en lo que te falta, anotá tres cosas que ya sabés hacer y que viajaron con vos.' },
  carousel: [
    { headline: 'Tres preguntas para empezar de nuevo sin borrar lo vivido', body: '' },
    { headline: '¿Qué te sostuvo hasta acá?', body: 'Nombrá una persona, un hábito o un lugar. Lo que te sostuvo antes puede volver a hacerlo, aunque cambie de forma.' },
    { headline: 'Guardalo para la próxima vez que dudes', body: 'Elegí una de las tres preguntas y respondela esta semana, por escrito.' },
  ],
  youtube: { kicker: 'Nuevo video', headline: 'Migrar sin perderte' },
  article: { kicker: 'Newsletter · Raíces', headline: 'Pertenecer no es llegar: es construir', body: 'Ideas y una acción concreta para quienes atraviesan un cambio de vida o de trabajo.' },
  webinar: { kicker: 'Webinar gratuito', headline: 'Raíces en movimiento', body: 'Una charla franca sobre empezar de nuevo sin borrar lo vivido.', cta: 'Inscribite con el enlace del texto' },
  explainer: { kicker: 'Nuevo video', headline: 'Cómo empezar de nuevo sin borrar lo vivido', highlights: ['vivido'] },
  highlight: { kicker: 'Etapas de cambio', headline: 'Lo que traje en la valija también cuenta', highlights: ['valija', 'cuenta'] },
  idea: { kicker: 'Para pensar', headline: '¿Qué te sostuvo hasta acá?', highlights: ['sostuvo'] },
  blocks: { kicker: 'Alejandra Cadario', headline: 'Cómo pensar diferente', highlights: [] },
  podcast: { kicker: 'Raíces en movimiento', episode: 'Episodio 1', headline: 'Empezar de nuevo en otro país', highlights: ['otro', 'país'] },
};
const COLLECTION = Object.entries(BRAND_TEMPLATES).filter(([, t]) => t.collection);
const CLASSIC = Object.entries(BRAND_TEMPLATES).filter(([, t]) => !t.collection);
const EXAMPLE_WHEN = webinarWhen({ date: '2026-11-12', time: '18:30', timeZone: 'America/Argentina/Buenos_Aires', extraZones: ['America/Mexico_City', 'Europe/Madrid'] });

/** An example piece of an original template, with a photo or only typography. */
function sampleOf(key, format, withPhoto, slide = 0) {
  const p = newPiece(key, null);
  p.format = format;
  const t = (text) => ({ text, origin: 'mine', refs: [] });
  if (BRAND_TEMPLATES[key].collection) {
    const { highlights, ...slots } = EXAMPLE[key];
    for (const [k, v] of Object.entries(slots)) p.content.slots[k] = t(v);
    p.content.style.highlights = highlights;
    p.content.slides = [
      { slots: { headline: t('Nombrá lo que ya sabés hacer'), body: t('Anotá tres cosas que viajaron con vos y que hoy te pueden servir.') } },
      { slots: { headline: t('Elegí una para esta semana'), body: t('Escribila y contale a alguien cómo la vas a usar.') } },
    ];
    p.content.cast = withPhoto ? 'both' : 'self';
    p.content.people = { self: { name: 'Alejandra Cadario', role: 'Anfitriona' }, guest1: { name: 'Nombre Invitada', role: 'Su rol' } };
    p.photos = { self: { photoId: 'example', crop: { x: 0.5, zoom: 1 }, frame: { ...SELF_FRAME_DEFAULT } }, guest1: { photoId: 'example', crop: { x: 0.5, y: 0.4, zoom: 1 } } };
    return p;
  }
  if (key === 'carousel') p.content.slides = EXAMPLE.carousel.map((x, i, all) => ({ kind: i === 0 ? 'cover' : i === all.length - 1 ? 'closing' : 'content', slots: { headline: t(x.headline), body: t(x.body) } }));
  else for (const [k, v] of Object.entries(EXAMPLE[key])) p.content.slots[k] = t(v);
  if (key === 'webinar') {
    p.content.people = { host: { name: 'Alejandra Cadario', role: 'Anfitriona' }, guest1: { name: 'Nombre Invitada', role: 'Su rol' } };
    p.photos = withPhoto ? { host: { photoId: 'example', crop: { x: 0.5, y: 0.45, zoom: 1 } } } : {};
  } else p.photos = withPhoto && slide === 0 ? { main: { photoId: 'example', crop: { x: 0.5, y: 0.5, zoom: 1 } } } : {};
  return p;
}

/** The photo of the catalog: your first own photo, or a drawn example image when there is none. */
function useSamplePhoto(photos) {
  const [sample, setSample] = useState(null);
  const mine = photos.find(p => p.kind === 'self' && p.hasImage && !p.archived);
  useEffect(() => {
    let on = true;
    if (mine) loadPhoto(mine.id).then(x => on && setSample({ ...x, photo: mine, own: true })).catch(() => on && setSample({ ...exampleImage(), photo: { kind: 'self' }, own: false }));
    else setSample({ ...exampleImage(), photo: { kind: 'self' }, own: false });
    return () => { on = false; };
  }, [mine?.id]);
  return sample;
}

/** The images of a catalog example of the collection: your own photo (or a drawn figure) and a drawn guest. */
const collectionImages = (sample) => ({
  self: sample?.own ? sample : { ...exampleFigure(0), photo: { kind: 'self' } },
  guest1: { ...exampleFigure(1), photo: { kind: 'guest', personName: 'Ejemplo', authorization: 'authorized' } },
});

/** One template of the collection in the formats of a channel, as an example. */
function CollectionExamples({ k, channel, brand, sample, cast = ['self', 'both'], width }) {
  const formats = BRAND_CHANNELS[channel].formats;
  // A carousel shows its cover and one slide after it
  const cells = formats.flatMap(f => (f === 'ig_carousel' ? [[f, 'self', 0], [f, 'self', 1]] : cast.map(c => [f, c, 0])));
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {cells.map(([f, c, slide]) => {
        const w = width || (BRAND_FORMATS[f].width > BRAND_FORMATS[f].height ? 300 : 200);
        const what = f === 'ig_carousel' ? (slide === 0 ? 'Carrusel · portada' : 'Carrusel · lámina 2') : c === 'self' ? 'Solo tu foto' : 'Con un invitado';
        return (
          <div key={`${f}-${c}-${slide}`} style={{ flex: `0 1 ${w}px`, minWidth: 0 }}>
            <div style={{ ...mutedSt, marginBottom: 4 }}>{what}</div>
            <PiecePreview piece={sampleOf(k, f, c === 'both')} brand={brand} images={collectionImages(sample)} maxWidth={w} slide={slide}
              label={`Ejemplo de ${BRAND_TEMPLATES[k].label}, ${BRAND_FORMATS[f].label}, ${what.toLowerCase()}`} />
          </div>
        );
      })}
    </div>
  );
}

export default function MyBrand({ ai }) {
  const [fontsReady, setFontsReady] = useState(false);
  useEffect(() => { let on = true; brandFontsReady().then(() => on && setFontsReady(true)); return () => { on = false; }; }, []);
  const [channel, setChannel] = useState('youtube');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('pieces');
  const [editing, setEditing] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [msg, setMsg] = useState('');
  const load = () => { setError(''); return ownerCall({ action: 'brandOverview' }).then(setData).catch(e => setError(e.message)); };
  useEffect(() => { load(); }, []);
  const [upgradeAsk, setUpgradeAsk] = useState(false);
  const sample = useSamplePhoto(data?.photos || []);
  const refreshPhotos = () => ownerCall({ action: 'brandListPhotos' }).then(d => setData(x => ({ ...x, photos: d.photos }))).catch(e => setError(e.message));

  if (error && !data) return <ErrorState message={error} onRetry={load} />;
  if (!data || !fontsReady) return <LoadingState />;
  const current = data.current;

  const open = async (id) => {
    setMsg('');
    try { const d = await ownerCall({ action: 'brandGetPiece', id }); setEditing({ piece: d.piece, webinar: d.webinar }); } catch (e) { setError(e.message); }
  };
  const create = (key, format = null) => {
    const piece = newPiece(key, current.id, format);
    // The collection starts with your first own photo, framed from the waist up
    const mine = BRAND_TEMPLATES[key].collection && data.photos.find(p => p.kind === 'self' && p.hasImage && !p.archived);
    if (mine) piece.photos = { self: { photoId: mine.id, crop: { x: 0.5, y: 0.5, zoom: 1 }, frame: { ...SELF_FRAME_DEFAULT } } };
    setEditing({ piece });
  };
  const brandNow = current?.profile || DEFAULT_BRAND_PROFILE;
  const channelPicker = (
    <div role="radiogroup" aria-label="Canal" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {Object.entries(BRAND_CHANNELS).map(([c, ch]) => <button key={c} type="button" role="radio" aria-checked={channel === c} style={chipSt(channel === c)} onClick={() => setChannel(c)}>{ch.label} · {ch.formats.map(f => `${BRAND_FORMATS[f].width} × ${BRAND_FORMATS[f].height}`).join(' y ')}</button>)}
    </div>
  );
  const upgradeOne = async (p) => { setMsg(''); try { await ownerCall({ action: 'brandUpgradePiece', id: p.id }); setMsg(`"${p.title || 'Pieza'}" usa el estilo aprobado. Tus textos y fotos no cambiaron.`); load(); } catch (e) { setError(e.message); } };
  const upgradeDrafts = async () => { setMsg(''); setUpgradeAsk(false); try { const d = await ownerCall({ action: 'brandUpgradeDrafts' }); setMsg(`${d.count} borrador${d.count === 1 ? '' : 'es'} actualizado${d.count === 1 ? '' : 's'} al estilo aprobado. Textos y fotos sin cambios.`); load(); } catch (e) { setError(e.message); } };
  const archive = async (p, archived) => { try { await ownerCall({ action: 'brandArchivePiece', id: p.id, archived }); load(); } catch (e) { setError(e.message); } };
  const del = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.title}"? No se puede deshacer. La plantilla original no cambia.`)) return;
    try { await ownerCall({ action: 'brandDeletePiece', id: p.id }); load(); } catch (e) { setError(e.message); }
  };

  if (editing) {
    return (
      <PieceEditor key={editing.piece.id || 'new'} initial={editing} brandVersions={data.versions} currentVersion={current} photos={data.photos} ai={ai}
        onClose={() => { setEditing(null); load(); }} onSaved={() => {}} onPhotosChanged={refreshPhotos} />
    );
  }

  const pieces = data.pieces.filter(p => showArchived || p.status !== 'archived');
  const isOld = (p) => (p.templateVersion || 1) < (BRAND_TEMPLATES[p.template]?.version || 1);
  const oldDrafts = data.pieces.filter(p => p.status === 'draft' && isOld(p));
  return (
    <div>
      <Notice icon="🌿" tone="private">
        <b>Mi marca · Alejandra Cadario.</b> La identidad (verde bosque, marfil, chocolate y mostaza; Georgia y Arial) se aplica solo a tus piezas. Oike no cambia.
        La IA propone y tú apruebas. Nada se publica ni se programa: exportas el PNG y copias el texto.
      </Notice>
      {!current && (
        <div style={{ ...warnSt, padding: 12 }}>
          Para crear piezas, guarda primero tu perfil de marca como versión 1 (ya trae la identidad aprobada). <button className="action-btn btn-primary" style={{ marginLeft: 6 }} onClick={() => setTab('profile')}>Ir al perfil</button>
        </div>
      )}
      <SubTabs tabs={[['pieces', 'Mis piezas'], ['templates', 'Plantillas originales'], ['profile', 'Perfil de marca'], ['photos', 'Fotos']]} value={tab} onChange={(t) => { setTab(t); setMsg(''); }} />
      {error && <div role="alert" style={{ color: '#B4362C', fontSize: 13, margin: '6px 0' }}>{error}</div>}
      {msg && <div role="status" style={{ color: '#1E7B4F', fontSize: 13, margin: '6px 0' }}>{msg}</div>}

      {tab === 'pieces' && (
        <div>
          <div style={{ ...cardSt, padding: 12 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>Nueva pieza para YouTube, LinkedIn o Instagram</h3>
            <div style={{ ...mutedSt, marginBottom: 8 }}>1. Elige el canal · 2. Elige la plantilla · 3. Edita textos, fotos y estilo · 4. Guarda la pieza · 5. Exporta el PNG. La plantilla original no cambia: cada pieza se guarda aparte y la puedes volver a editar.</div>
            {channelPicker}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginTop: 10 }}>
              {COLLECTION.map(([k, t]) => {
                const f = BRAND_CHANNELS[channel].formats[0];
                return (
                  <div key={k} style={{ border: '1px solid var(--globant-border)', borderRadius: 8, padding: 10, display: 'grid', gap: 6, alignContent: 'start' }}>
                    <b style={{ fontSize: 14 }}>{t.label}</b>
                    <PiecePreview piece={sampleOf(k, f, false)} brand={brandNow} images={collectionImages(sample)} maxWidth={BRAND_FORMATS[f].width > BRAND_FORMATS[f].height ? 300 : 220} label={`Ejemplo de ${t.label}`} />
                    <div style={mutedSt}>{t.description}</div>
                    <div><button className="action-btn btn-primary" disabled={!current} onClick={() => create(k, f)}>Usar esta plantilla</button></div>
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '10px 0' }}>
            <span style={{ ...mutedSt, alignSelf: 'center' }}>Otras plantillas:</span>
            {CLASSIC.map(([k, t]) => <button key={k} className="action-btn" disabled={!current} onClick={() => create(k)}>+ {t.label}</button>)}
          </div>
          {oldDrafts.length > 0 && (
            <div style={{ ...warnSt, padding: 10 }}>
              {oldDrafts.length === 1 ? 'Un borrador usa' : `${oldDrafts.length} borradores usan`} el estilo anterior. Actualizar cambia solo el estilo (verde bosque, marfil y mostaza): tus textos, fotos y recortes quedan igual. Las piezas aprobadas se actualizan una por una.
              <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {!upgradeAsk ? <button className="action-btn" onClick={() => setUpgradeAsk(true)}>Actualizar {oldDrafts.length === 1 ? 'el borrador' : `los ${oldDrafts.length} borradores`} al estilo aprobado</button> : (
                  <>
                    <button className="action-btn btn-primary" onClick={upgradeDrafts}>Sí, actualizar {oldDrafts.length === 1 ? 'el borrador' : `los ${oldDrafts.length}`}</button>
                    <button className="action-btn" onClick={() => setUpgradeAsk(false)}>Cancelar</button>
                  </>
                )}
              </div>
            </div>
          )}
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, margin: '4px 0 10px' }}><input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} /> Mostrar archivadas</label>
          {!pieces.length ? <EmptyState title="Todavía no hay piezas">Elige una plantilla para crear la primera. Cada pieza es una copia editable: la plantilla original no cambia.</EmptyState> : (
            <div style={{ display: 'grid', gap: 8 }}>
              {pieces.map(p => {
                const t = BRAND_TEMPLATES[p.template];
                return (
                  <div key={p.id} style={{ ...cardSt, marginBottom: 0, padding: 12, display: 'flex', gap: 10, justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ minWidth: 0, flex: '1 1 260px' }}>
                      <button type="button" onClick={() => open(p.id)} style={{ font: 'inherit', fontWeight: 700, fontSize: 15, background: 'none', border: 'none', padding: 0, color: 'var(--globant-text)', cursor: 'pointer', textAlign: 'left' }}>{p.title || 'Pieza sin título'}</button>
                      {p.headline && <div style={{ ...mutedSt, overflowWrap: 'anywhere' }}>{p.headline}</div>}
                      <div style={{ ...mutedSt, marginTop: 2 }}>Desde la plantilla original {t?.label || p.template} (v{p.templateVersion}) · {BRAND_FORMATS[p.format]?.label} · marca v{p.brandVersion}{p.updatedAt ? ` · ${fmtDate(p.updatedAt)}` : ''}{p.exportedAt ? ` · exportada ${fmtDate(p.exportedAt)}` : ''}</div>
                      {p.eventChanged && <div style={{ fontSize: 12, color: '#9A6212', marginTop: 2 }}>⚠ El evento cambió: actualiza esta invitación.</div>}
                      {p.hasAiDraft && <div style={mutedSt}>Con propuesta de IA guardada aparte.</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      {isOld(p) && <span style={{ ...badgeSt(false), color: '#7A4D0E' }}>Estilo anterior</span>}
                      {isOld(p) && p.status !== 'archived' && <button className="action-btn" onClick={() => upgradeOne(p)}>Actualizar al estilo aprobado</button>}
                      <span style={badgeSt(p.status === 'approved')}>{STATUS[p.status] || p.status}</span>
                      <button className="action-btn" onClick={() => open(p.id)}>Abrir</button>
                      <button className="action-btn" onClick={() => archive(p, p.status !== 'archived')}>{p.status === 'archived' ? 'Recuperar' : 'Archivar'}</button>
                      <button className="action-btn btn-danger" onClick={() => del(p)}>Eliminar</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === 'templates' && (
        <div>
          <p style={{ ...mutedSt, marginTop: 0 }}>
            Las plantillas originales <b>no se editan</b> desde aquí: cada pieza que creas es una copia que guarda de qué plantilla y de qué versión salió.
            Estilo aprobado: fondo verde bosque, títulos y textos marfil, detalles mostaza. La foto es opcional: sin foto, la pieza es solo tipográfica.
            Los textos son de ejemplo{sample && !sample.own ? ' y la imagen es un dibujo de ejemplo (sube una foto tuya en Fotos para verla aquí)' : sample?.own ? ' y la foto es la primera tuya' : ''}.
          </p>
          <div style={{ ...cardSt, padding: 12 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>Colección YouTube, LinkedIn e Instagram</h3>
            <div style={{ ...mutedSt, marginBottom: 8 }}>Cinco diseños en los tres canales (YouTube, LinkedIn e Instagram, con carrusel). Títulos en sans serif gruesa (Montserrat); fondo verde bosque, textos marfil y acentos mostaza. Las fotos se adaptan a cuántas personas aparecen. Las figuras dibujadas son de ejemplo{sample?.own ? '; tu foto es la primera que cargaste' : ''}.</div>
            {channelPicker}
            <div style={{ display: 'grid', gap: 16, marginTop: 10 }}>
              {COLLECTION.map(([k, t]) => (
                <div key={k}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}><b>{t.label}</b><span style={badgeSt(false)}>Original · v{t.version}</span></div>
                  <div style={{ ...mutedSt, margin: '4px 0 8px' }}>{t.description}</div>
                  <CollectionExamples k={k} channel={channel} brand={brandNow} sample={sample} />
                  <div style={{ marginTop: 8 }}><button className="action-btn btn-primary" disabled={!current} onClick={() => create(k, BRAND_CHANNELS[channel].formats[0])}>Crear pieza desde esta plantilla</button></div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'grid', gap: 16 }}>
            {CLASSIC.map(([k, t]) => (
              <div key={k} style={{ ...cardSt, marginBottom: 0, padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}><b>{t.label}</b><span style={badgeSt(false)}>Original · v{t.version}</span></div>
                <div style={{ ...mutedSt, margin: '4px 0 8px' }}>{t.description}</div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  {t.formats.flatMap(f => (k === 'carousel' ? [[f, true, 0], [f, false, 1]] : [[f, true, 0], [f, false, 0]])).map(([f, withPhoto, slide]) => (
                    <div key={`${f}-${withPhoto}-${slide}`} style={{ flex: `0 1 ${BRAND_FORMATS[f].width > BRAND_FORMATS[f].height ? 300 : 210}px`, minWidth: 0 }}>
                      <div style={{ ...mutedSt, marginBottom: 4 }}>{k === 'carousel' ? (slide === 0 ? 'Portada con foto' : 'Lámina de contenido') : withPhoto ? 'Con foto' : 'Solo tipografía'}</div>
                      <PiecePreview piece={sampleOf(k, f, withPhoto, slide)} brand={current?.profile || DEFAULT_BRAND_PROFILE} slide={slide}
                        images={withPhoto && sample ? { [k === 'webinar' ? 'host' : 'main']: sample } : {}} when={k === 'webinar' ? EXAMPLE_WHEN : null}
                        maxWidth={BRAND_FORMATS[f].width > BRAND_FORMATS[f].height ? 300 : 210} label={`Ejemplo de ${t.label}${withPhoto ? ' con foto' : ' solo tipográfica'}`} />
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 10 }}><button className="action-btn btn-primary" disabled={!current} onClick={() => create(k)}>Crear pieza desde esta plantilla</button></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'profile' && <BrandProfile key={current?.id || 'none'} current={current} versions={data.versions} onSaved={() => load()} />}
      {tab === 'photos' && <BrandPhotos photos={data.photos} onChange={refreshPhotos} />}
    </div>
  );
}
